import React, { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { IGif, INicheMap } from "services/types";
import { useForYouFeedQuery } from "queries";
import { PreviewFeed } from "components/feed/PreviewFeed";
import { SuggestedNiches } from "components/widgets/SuggestedNiches";
import { SuggestedCreators } from "components/widgets/SuggestedCreators";

import "./PersonalFeed.scss";
import { filterGifsList } from "utils/filterGifsList";
import { filterNichesList } from "utils/filterNichesList";
import { IUserList } from "components/feed/GifList";
import { filterUsersList } from "utils/filterUsersList";
import { ViewRegion } from "utils/metrics";
import { FeedAds } from "components/ads/FeedAds";
import { MobileContentPageScrollKeys } from "components/ads/config/units/contentPageScrollMobile";
import { DesktopContentPageScrollKeys } from "components/ads/config/units/contentPageScrollDesktop";

const NICHES_POSITION = 9;
const CREATORS_POSITION = 4;

export const PersonalFeed = () => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [gifs, setGifs] = useState<IGif[]>([]);
  const [users, setUsers] = useState<IUserList>({});
  const [niches, setNiches] = useState<INicheMap>({});

  const { data, isSuccess, isError, isLoading } = useForYouFeedQuery({
    page: page,
    count: 50,
  });

  useEffect(() => {
    if (isSuccess && data !== undefined) {
      setGifs(prev => filterGifsList(prev, data.gifs));
      setPages(data.pages);
      setUsers(prev => filterUsersList(prev, data.users));

      if (data.niches) {
        setNiches(prev => filterNichesList(prev, data.niches));
      }
    }
  }, [data, isSuccess]);

  const hasMore = useMemo(() => page < pages, [page, pages]);

  const fetchMore = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
  }, []);

  const injector = useCallback((pos: number, gif: IGif, fullscreen: boolean): JSX.Element => {
    const children: ReactNode[] = [];

    if (!fullscreen && pos === NICHES_POSITION) {
      children.push(<SuggestedNiches key="SuggestedNiches" page={2}/>);
    }

    if (!fullscreen && pos === CREATORS_POSITION) {
      children.push(<SuggestedCreators key="SuggestedCreators" />);
    }

    children.push(
      <FeedAds
        key={`feedAds${gif.id}${pos}`}
        position={pos}
        fullscreen={fullscreen}
        mobileAds={MobileContentPageScrollKeys}
        desktopAds={DesktopContentPageScrollKeys}
      />
    );

    return <Fragment>{children}</Fragment>;
  }, []);

  return (
    <div className="homeFeed">
      <SuggestedNiches page={1}/>

      {isError && <p className="error">Error loading gifs. Please refresh the page.</p>}

      <PreviewFeed
        gifs={gifs}
        users={users}
        niches={niches}
        hasMore={hasMore}
        fetchMore={fetchMore}
        injector={injector}
        source={ViewRegion.ForYou}
        isLoading={isLoading}
      />
    </div>
  );
};
